import {
	Box,
	Button,
	ColumnLayout,
	Container,
	Form,
	Header,
	Popover,
	SpaceBetween,
	Textarea
} from '@cloudscape-design/components'
import Icon from '@cloudscape-design/components/icon'

import { useOktaAuth } from '@okta/okta-react'
import { FC, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import FormInput from '../../../components/ControlledFormInputs/Input'
import FormSelect from '../../../components/ControlledFormInputs/Select'
import FormInputArea from '../../../components/ControlledFormInputs/TextArea'

import { useNewApiFormStore } from '../../../stores/apis/newApiFormStore'
import { useGenericTagsManagementFormStore } from '../../../stores/tags/genericTagsManagementFormStore'
import useCreateNewApi from '../../../hooks/mutate/useCreateNewApi'
import useApprovedGcpProjects from '../../../hooks/query/useApprovedGcpProjects'
import CONSTANTS from '../../../constants'
import { gcpProject } from '../../../types/gcpProject'
import type { ApiRequest } from '../../../types/apis'
import api from '../../../api'
import { getTagsApprovalObject, TagsRequest } from '../../../types/tags'
import { v4 as uuidv4 } from 'uuid'
import useCreateNewTags from '../../../hooks/mutate/useCreateNewTags'
import useMicroservices from '../../../hooks/query/useMicroservices'
import useApplications from '../../../hooks/query/useApplications'

const METHOD_LIST = [
	{ label: 'GET', value: 'GET' },
	{ label: 'POST', value: 'POST' },
	{ label: 'PUT', value: 'PUT' },
	{ label: 'PATCH', value: 'PATCH' },
	{ label: 'DELETE', value: 'DELETE' }
]

const CODE_TEMPLATE_LIST = [
	// { label: 'NodeJS', value: 'nodejs' },
	{ label: 'NodeJS + CXS', value: 'cxs-nodejs' }
]

const NewApiForm: FC = () => {

	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string

	const [microserviceOptions, setMicroserviceOptions] = useState<
		{ label: string; value: string }[]
	>([])
	const { data: microservices } = useMicroservices()

	const [applicationOptions, setApplicationOptions] = useState<
		{ label: string; value: string }[]
	>([])
	const { data: applications } = useApplications()

	const navigate = useNavigate()
	const {
		formValues,
		actions: { setFormValues, clearFormValues }
	} = useNewApiFormStore()

	const {
		formValues: tagFormValues,
		actions: {
			setFormValues: setTagFormValues,
			clearFormValues: clearTagFormValues
		}
	} = useGenericTagsManagementFormStore()

	const { mutate: createNewApi, isLoading } = useCreateNewApi()
	const { mutate: createTags, isLoading: isCreateTagsLoading } =
		useCreateNewTags()
	const {
		control,
		setValue,
		trigger,
		formState: { isValid },
		watch
	} = useForm({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: formValues
	})

	interface envOpt {
		label: string
		value: string
	}

	const [isInheritTagsChecked, setInheritTagsChecked] = useState(false)

	const [popFields, setPopFields] = useState(false)

	const [isLoadingApplications, setIsLoadingApplications] = useState(true);
	const [isLoadingMicroservices, setIsLoadingMicroservices] = useState(true);

	const [isRequestAndResponseDisabled, setIsRequestAndResponseDisabled] = useState(false);	

	useEffect(() => {
		if (microservices && microservices.length > 0) {
			const filteredMicroservices = microservices.filter((microservice) => {
				return (
					microservice.status === "APPROVED" &&
					microservice.applicationName.label === formValues.applicationName &&
					microservice.isMerged == true
				);
			});
			console.log("Filtered Microservices:", filteredMicroservices);

			const uniqueMicroservices: envOpt[] = []
			const microserviceNames = new Set()

			filteredMicroservices.forEach((microservice) => {
				if (!microserviceNames.has(microservice.microserviceName)) {
					microserviceNames.add(microservice.microserviceName)
					uniqueMicroservices.push({
						label: microservice.microserviceName,
						value: microservice.microserviceID
					})
				}
			})

			const extractedOptions = uniqueMicroservices
			setMicroserviceOptions(extractedOptions)
			setIsLoadingMicroservices(false)
		}

	}, [microservices, formValues.applicationName])

	// Use Applications for Dropdwon
	useEffect(() => {
		if (applications && applications.length > 0) {
			const filteredApplications = applications.filter(
				(application) => application.status === 'APPROVED'
			)
			const uniqueApplications: envOpt[] = []
			const applicationNames = new Set()

			filteredApplications.forEach((application) => {
				if (!applicationNames.has(application.details?.applicationName)) {
					applicationNames.add(application.details?.applicationName)
					uniqueApplications.push({
						label: application.details?.applicationName,
						value: application.applicationID
					})
				}
			})

			const extractedOptions = uniqueApplications
			setApplicationOptions(extractedOptions)
			setIsLoadingApplications(false)
		}
	}, [applications])

	// useEffect(() => {
	// 	const getProjectWithTags = async (gcpProjectID: {
	// 		label: string
	// 		value: string
	// 	}) => {
	// 		let response = await api.gcpProjects.getSinglegcpProject(
	// 			gcpProjectID.value,
	// 			true
	// 		)
	// 		let project = response.body[0] as gcpProject
	// 		console.log(project)

	// 		setTagFormValues('platformName', project.tags?.details.platformName)
	// 		setTagFormValues('group', project.tags?.details.group)
	// 		setTagFormValues(
	// 			'fundingSourceType',
	// 			project.tags?.details.fundingSourceType
	// 		)
	// 		setTagFormValues('WBSCode', project.tags?.details.WBSCode)
	// 		setTagFormValues('costCenter', project.tags?.details.costCenter)
	// 		setTagFormValues('projectName', gcpProjectID.label)

	// 		setFormValues('wbsCode', project.tags?.details.WBSCode)
	// 		setFormValues('costCenter', project.tags?.details.costCenter)
	// 		setFormValues('group', project.tags?.details.group)
	// 		setValue('group', project.tags?.details.group)
	// 	}
	// 	if (isInheritTagsChecked) {
	// 		console.log(formValues.gcpProjectID)
	// 		getProjectWithTags(formValues.gcpProjectID)
	// 	} else {
	// 		clearTagFormValues()

	// 		setFormValues('wbsCode', '')
	// 		setFormValues('costCenter', '')
	// 		setFormValues('group', '')
	// 		setValue('group', '')
	// 	}
	// }, [
	// 	formValues.gcpProjectID,
	// 	setTagFormValues,
	// 	setValue,
	// 	isInheritTagsChecked,
	// 	clearTagFormValues,
	// 	setFormValues
	// ])

	// useEffect(() => {
	// 	if (tagFormValues.WBSCode !== '') {
	// 		setFormValues('wbsCode', tagFormValues.WBSCode)
	// 	}

	// 	if (tagFormValues.costCenter !== '') {
	// 		setFormValues('costCenter', tagFormValues.costCenter)
	// 	}
	// }, [tagFormValues.WBSCode, tagFormValues.costCenter, setFormValues])

	// useEffect(() => {
	// 	if (formValues.platformOwner !== '') {
	// 		setTagFormValues('platformOwner', formValues.platformOwner)
	// 	}

	// 	if (formValues.platformName !== '') {
	// 		setTagFormValues('platformName', formValues.platformName)
	// 	}

	// 	if (formValues.systemAlias !== '') {
	// 		setTagFormValues('systemAlias', formValues.systemAlias)
	// 	}
	// }, [formValues, setTagFormValues])

	const handleSubmit = async () => {
		await trigger()
		if (!isValid) return

		let tagsId = uuidv4()

		let newTag: TagsRequest = {
			requestorID: thisUserEmail,
			tagsID: tagsId,
			approvalStatus: getTagsApprovalObject(),
			details: tagFormValues,
			action: 'CREATE',
			status: 'PENDING_APPROVAL'
		}

		const api: ApiRequest = {
			requestorID: thisUserEmail,
			// tagsID: tagsId,
			action: 'CREATE',
			details: formValues,
			status: 'PENDING_APPROVAL',
			creationTime: new Date().toISOString()
		}

		createTags(newTag, {
			onSuccess: () => {
				createNewApi(api, {
					onSuccess: () => {
						navigate('/apis')
						clearFormValues()
						clearTagFormValues()
						window.location.reload()
					},
					onError: (e) => console.error(e)
				})
			},
			onError: (e) => console.error(e)
		})
	}
	useEffect(() => {
		setValue('region', 'Singapore (asia-southeast1)')
	}, [setValue])

	// useEffect(() => {
	// 	if (formValues?.platformType?.value === 'standard') {
	// 		setPopFields(true)
	// 	} else {
	// 		setPopFields(false)
	// 	}
	// }, [formValues?.platformType?.value, setPopFields, formValues])

	useEffect(() => {
		const subscription = watch((value, { name }) => {
			if (name === "applicationName") {
				setValue("microserviceName", "");
			}
		});

		return () => subscription.unsubscribe();
	}, [watch, setValue]);


	// TEMPORARY APPLICATION VALUE FOR BETA API ONBOARDING - CXS
	useEffect(() => {
		if (applicationOptions.length > 0) {
			const cxsOption = applicationOptions.find(
				option => option.value === 'cxs'
			) || applicationOptions[0]

			setValue('applicationName', cxsOption)
			setFormValues('applicationName', cxsOption)
		}
	}, [applicationOptions, setValue, setFormValues])

	// TEMPORARY CODE TEMPLATE VALUE FOR BETA API ONBOARDING - CXS
	useEffect(() => {
		const subscription = watch((value, { name }) => {
			if (name === 'microserviceName') {
				const selectedMicroserviceId = value.microserviceName?.value;

				if (selectedMicroserviceId) {
					const selectedMicroservice = microservices?.find(
						(ms) => ms.microserviceID === selectedMicroserviceId
					);

					if (selectedMicroservice) {
						const templateOption = CODE_TEMPLATE_LIST[0];
						setFormValues('codeTemplate', templateOption);
						setValue('codeTemplate', templateOption, {
							shouldValidate: true,
							shouldDirty: true
						});
					}
				}
			}
		});

		return () => subscription.unsubscribe();
	}, [watch, microservices, setFormValues, setValue]);

	// GET PLATFORM NAME
	useEffect(() => {
		const subscription = watch((value) => {
			const selectedApplicationId = value.applicationName?.value; 
			if (selectedApplicationId && applications) {
				// Find the selected application from the application list
				const selectedApplication = applications.find(
					(app) => app.applicationID === selectedApplicationId
				);
				if (selectedApplication) {
					const platformName = selectedApplication.details.platformName;

					setFormValues('platformName', platformName || '');
				}
			}
		});
		return () => subscription.unsubscribe();
	}, [applications, setFormValues, watch]);

	useEffect(() => {
		setIsRequestAndResponseDisabled(
		  formValues.httpMethod?.value === 'GET' || formValues.httpMethod?.value === 'DELETE'
		);
	  }, [formValues.httpMethod]);

	return (
		<Form
			actions={
				<SpaceBetween direction="horizontal" size="xs">
					<Box>
						<Button
							variant="primary"
							onClick={handleSubmit}
							loading={isLoading || isCreateTagsLoading}
						>
							Submit
						</Button>
						<Button variant="link" onClick={() => navigate(-1)}>
							Cancel
						</Button>
					</Box>
				</SpaceBetween>
			}
			header={
				<Header variant="h1" description="">
					Create Endpoint
				</Header>
			}
		>
			<SpaceBetween direction="vertical" size="s">
				<Container>
					<SpaceBetween direction="vertical" size="l">
						<ColumnLayout columns={2} variant="default">
							<div
								style={{
									display: 'flex',
									alignItems: 'center'
								}}
							>
								<div style={{ width: '650px' }}>
									<FormSelect
										label="Application Name"
										name="applicationName"
										description='Select the application your microservice is under'
										control={control}
										rules={{
											required: 'Please select an option'
										}}
										setFormValues={setFormValues}
										options={isLoadingApplications
											? [{ label: 'Loading...', value: '' }]
											: applicationOptions.length === 0
												? [{ label: 'No applications to show', value: '' }]
												: applicationOptions
										}
										placeholder="Select Application"
										disabled={true}
									/>
								</div>
							</div>
							<FormSelect
								label="Microservice"
								name="microserviceName"
								control={control}
								rules={{
									required: 'Please select an option'
								}}
								description='Select the microservice your API will be under'
								setFormValues={setFormValues}
								options={
									isLoadingMicroservices
										? [{ label: 'Loading...', value: '' }]
										: microserviceOptions.length === 0
											? [{ label: 'No microservices to show', value: '' }]
											: microserviceOptions
								}
								placeholder="Select Microservice Name"
							/>
							{/* <div></div> */}
							<FormInput
								name="apiName"
								label="Name"
								type="text"
								control={control}
								setFormValues={setFormValues}
								placeholder="Enter Display Name of API (e.g. GetPetName)"
								description="Enter the API name in pascal case"
								rules={{
									required: 'Please enter API name',
									minLength: {
										value: 5,
										message: 'Minumum length of 5 characters'
									},
									pattern: {
										value: /^[A-Z][a-z]+(?:[A-Z][a-z]+)*$/,
										message:
											'API name should be pascal case only and whitespaces are not allowed.'
									}
								}}
							/>
							<FormSelect
								label="Code Template"
								name="codeTemplate"
								control={control}
								description='Select the API code template'
								setFormValues={setFormValues}
								options={CODE_TEMPLATE_LIST}
								placeholder="Select Code Template"
								disabled={true}
								rules={{
									required: 'Please select a Code Template'
								}}
							/>
							<FormInput
								name="apiEndpoint"
								label="Path"
								type="text"
								control={control}
								setFormValues={setFormValues}
								placeholder="Enter Endpoint Path"
								description="The endpoint URL/path"
								rules={{
									required: 'Please enter a path'
								}}
							/>
							<FormSelect
								label="HTTP Method"
								name="httpMethod"
								control={control}
								rules={{
									required: 'Please select an option'
								}}
								setFormValues={setFormValues}
								options={METHOD_LIST}
								placeholder="Select HTTP Method"
								description="Please select the HTTP method"

							/>
							<FormInput
								name="apiVersion"
								label="API Version"
								type="text"
								control={control}
								setFormValues={setFormValues}
								placeholder="e.g., v1, v2"
								description="Enter the API Version"
								rules={{
									required: 'Please enter version',
									pattern: {
										value: /^v\d+(\.\d+)*$/,
										message: 'Input should be in the format v1, v1.0, v1.1.0, etc.'
									}
								}}
							/>
							<div style={ {display: 'none' }}>
								<FormInput
									name="platformName"
									label="Platform Name"
									type="text"
									control={control}
									setFormValues={setFormValues}
								/>
							</div>
						</ColumnLayout>
						<div style={{ display: 'flex', flexDirection: 'column', width: '150%' }}>
							<FormInputArea
								label="Description"
								name="description"
								control={control}
								setFormValues={setFormValues}
								placeholder="Enter description"
								description="Please describe the function of your API"

								rules={{
									required: 'Please enter a description'
								}}

							/>
						</div>
						<ColumnLayout columns={2} variant="default">
							<div style={{ display: 'flex', flexDirection: 'column' }}>
								<FormInputArea
									label="Mock Request"
									name="mockRequest"
									control={control}
									setFormValues={setFormValues}
									placeholder="Enter mock request data"
									description="Please enter the mock request in a valid JSON format"
									disabled={isRequestAndResponseDisabled}
									rules={{
										validate: {
											jsonFormat: (value) => {
												try {
													JSON.parse(value);
													return true;
												} catch {
													return "Please enter valid JSON format";
												}
											}
										}
									}}
								/>
							</div>
							<div style={{ display: 'flex', flexDirection: 'column' }}>
								<FormInputArea
									label="Mock Response"
									name="mockResponse"
									control={control}
									setFormValues={setFormValues}
									placeholder="Enter Mock Response"
									description="Please enter the mock response in a valid JSON format"
									disabled={isRequestAndResponseDisabled}
									rules={{
										validate: {
											jsonFormat: (value) => {
												try {
													JSON.parse(value);
													return true;
												} catch {
													return "Please enter valid JSON format";
												}
											}
										}
									}}
								/>
							</div>
						</ColumnLayout>
					</SpaceBetween>
				</Container>
			</SpaceBetween>
		</Form>
	)
}

export default NewApiForm
